"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Typeahead = void 0;
var React = _interopRequireWildcard(require("react"));
var _react2 = require("@floating-ui/react");
var _size = require("../../styles/variables/_size");
var _space = require("../../styles/variables/_space");
var _classify = require("../../utils/classify");
var _clickAway = require("../../utils/click-away");
var _mergeRefs = require("../../utils/merge-refs");
var _Menu = require("../Menu");
var _SearchInput = require("../SearchInput");
var _TypeaheadModule = _interopRequireDefault(require("./Typeahead.module.css"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
const BaseTypeahead = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    size = 'medium',
    classNames,
    placeholder = 'Select...',
    onSelect,
    onSearch,
    onClear,
    menu,
    onMenuOpen,
    onMenuClose,
    typeaheadInputText = '',
    isLoading,
    menuOpenOffset = 1,
    onFocus,
    clickAwayRef,
    ...inputProps
  } = _ref;
  const menuOptions = menu?.options;
  const {
    x,
    y,
    refs,
    strategy
  } = (0, _react2.useFloating)({
    open: true,
    strategy: 'absolute',
    placement: 'bottom-start',
    whileElementsMounted: _react2.autoUpdate,
    middleware: [(0, _react2.flip)(), (0, _react2.offset)(parseInt(_space.spaceXXSmall))]
  });
  const onMenuToggle = isOpen => {
    isOpen ? onMenuOpen && onMenuOpen() : onMenuClose && onMenuClose();
  };
  return /*#__PURE__*/React.createElement(_clickAway.ClickAway, {
    onChange: onMenuToggle,
    clickAwayRef: clickAwayRef
  }, _ref2 => {
    let {
      isOpen,
      onOpen,
      clickAway,
      boundaryRef,
      triggerRef
    } = _ref2;
    return /*#__PURE__*/React.createElement("div", {
      "data-testid": "Typeahead",
      className: (0, _classify.classify)(_TypeaheadModule.default.typeaheadContainer, classNames?.wrapper),
      ref: boundaryRef
    }, /*#__PURE__*/React.createElement(_SearchInput.SearchInput, _extends({}, inputProps, {
      ref: ref,
      boxRef: (0, _mergeRefs.mergeRefs)([refs.setReference, triggerRef]),
      size: size,
      placeholder: placeholder,
      value: typeaheadInputText,
      classNames: {
        box: classNames?.box
      },
      isLoading: isLoading,
      onChange: e => {
        e.stopPropagation();
        onSearch && onSearch(e);
        if (e.target.value.length >= menuOpenOffset) {
          !isOpen && onOpen();
        } else {
          clickAway();
        }
      },
      onFocus: _e => {
        if (typeaheadInputText.length >= menuOpenOffset) {
          !isOpen && onOpen();
        } else {
          clickAway();
        }
        onFocus?.(_e);
      },
      onClear: _e => {
        onClear?.();
      }
    })), isOpen && !isLoading && menu && menuOptions && !!menuOptions.length && /*#__PURE__*/React.createElement("div", {
      ref: refs.setFloating,
      style: {
        position: strategy,
        top: y ?? _space.spaceNone,
        left: x ?? _space.spaceNone,
        width: _size.sizeFluid
      }
    }, /*#__PURE__*/React.createElement(_Menu.Menu, _extends({}, menu, {
      options: menuOptions,
      onSelect: (option, e) => {
        onSelect && onSelect(option, e);
        if (
        // option.keepMenuOpenOnOptionSelect - to allow the menu persist its open stat upon option selection in normal variant
        !option.keepMenuOpenOnOptionSelect && (!menu.optionsVariant || menu.optionsVariant === 'normal')) {
          clickAway();
        }
      },
      size: menu.size || size,
      onTabOut: clickAway
    }))));
  });
});
const StatefulTypeahead = /*#__PURE__*/React.forwardRef((_ref3, ref) => {
  let {
    menu,
    ...props
  } = _ref3;
  const {
    typeaheadInputText = ''
  } = props;
  const [filteredOptions, setFilteredOptions] = React.useState(menu?.options);
  React.useEffect(() => {
    const optionsFiltered = menu?.options && menu.options.filter(option => {
      if (!option.label || !typeaheadInputText) {
        return true;
      } else {
        return option.label.toLowerCase().indexOf(typeaheadInputText.toLowerCase()) !== -1;
      }
    });
    setFilteredOptions(optionsFiltered || []);
  }, [typeaheadInputText, menu?.options]);
  return /*#__PURE__*/React.createElement(BaseTypeahead, _extends({}, props, {
    menu: {
      ...menu,
      options: filteredOptions
    },
    ref: ref
  }));
});
const StatelessTypeahead = /*#__PURE__*/React.forwardRef((props, ref) => /*#__PURE__*/React.createElement(BaseTypeahead, _extends({}, props, {
  ref: ref
})));
const Typeahead = /*#__PURE__*/React.forwardRef((_ref4, ref) => {
  let {
    allowInternalFilter = true,
    ...props
  } = _ref4;
  if (allowInternalFilter) {
    return /*#__PURE__*/React.createElement(StatefulTypeahead, _extends({}, props, {
      ref: ref
    }));
  } else {
    return /*#__PURE__*/React.createElement(StatelessTypeahead, _extends({}, props, {
      ref: ref
    }));
  }
});
exports.Typeahead = Typeahead;